/**
 * GrowthCandidates — iter100 · Vue dédiée candidature Growth
 *
 * **iter106 CEO 2026-09-11** :
 *  - Bandeau stats compact V1..V5 + Total à préqualifier
 *  - Bloc "Prochain candidat à appeler" (auto-géré par NextToCallBlock)
 *
 * **iter107 CEO 2026-09-11** : Doctrine 4 catégories.
 *  - Bandeau stats simplifié : Total / Analysés / À analyser / À appeler
 *    + ligne TOP / TRÈS PROMETTEUR / POTENTIEL À VÉRIFIER / NON PRIORITAIRE
 *  - Plus de V1..V5 visibles (restent en backend pour l'ordre file)
 *  - Seuils recalibrés : TOP≥70, PROMISING 55-69, POTENTIAL 40-54, LOW<40
 *
 * **Brief CEO §13** :
 *  - Vue dédiée aux candidatures Business Development & Growth Associate
 *  - Filtres : TOP / TRÈS PROMETTEUR / POTENTIEL / NON PRIORITAIRE / À scorer
 *  - Classement par défaut : SCORE GROWTH DÉCROISSANT
 *  - « Qui dois-je traiter en premier ? » — visible immédiatement
 */
import { motion } from "framer-motion";
import { CalendarClock, FileText, Loader2, Sparkles, Zap } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import GrowthCandidateCard from "../components/growth/GrowthCandidateCard";
import NextToCallBlock from "../components/growth/NextToCallBlock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { hasRole } from "../lib/roles";


const CATEGORY_FILTERS = [
    { key: "",          label: "Tous",                emoji: "" },
    { key: "top",       label: "TOP PROFIL",          emoji: "⭐" },
    { key: "promising", label: "Très prometteurs",    emoji: "🟢" },
    { key: "potential", label: "Potentiel à vérifier", emoji: "🟡" },
    { key: "low",       label: "Non prioritaires",    emoji: "🔴" },
    { key: "unscored",  label: "À scorer",            emoji: "⏳" },
];

// iter107 · CEO 2026-09-11 · DOCTRINE VISIBLE : 4 catégories uniquement.
// Les V1..V5 restent en backend pour l'ordre de la file mais ne sont plus
// affichées à l'utilisateur.
const CATEGORY_STATS = [
    { key: "top",       label: "TOP PROFIL",           emoji: "⭐", range: "≥ 70",   tone: "bg-yellow-50 text-yellow-900 border-yellow-300" },
    { key: "promising", label: "TRÈS PROMETTEUR",      emoji: "🟢", range: "55-69",  tone: "bg-emerald-50 text-emerald-900 border-emerald-300" },
    { key: "potential", label: "POTENTIEL À VÉRIFIER", emoji: "🟡", range: "40-54",  tone: "bg-amber-50 text-amber-900 border-amber-300" },
    { key: "low",       label: "NON PRIORITAIRE",      emoji: "🔴", range: "< 40",   tone: "bg-slate-100 text-slate-600 border-slate-300" },
];


export default function GrowthCandidates() {
    const { user } = useAuth();
    const isAdmin = hasRole(user, "admin");

    const [candidates, setCandidates] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [batchRunning, setBatchRunning] = useState(false);
    const [filter, setFilter] = useState("");
    const [nextToCallReloadKey, setNextToCallReloadKey] = useState(0);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filter === "unscored") {
                params.set("unscored", "true");
            } else if (filter) {
                params.set("category", filter);
            }
            params.set("limit", "200");
            const [candRes, analyticsRes] = await Promise.all([
                api.get(`/growth/candidates?${params.toString()}`),
                api.get(`/growth/analytics`),
            ]);
            setCandidates(candRes.data || []);
            setAnalytics(analyticsRes.data);
            toast.dismiss("growth-load-error");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de chargement", {
                id: "growth-load-error",
            });
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    // iter109b · CEO 2026-09-12 · Reset l'historique de navigation prequal
    // dès qu'on revient sur /growth, pour repartir propre au prochain
    // "Appeler & préqualifier" (sinon on cycle sur les mêmes candidats).
    useEffect(() => {
        try { sessionStorage.removeItem("growth_prequal_session_history"); }
        catch { /* silencieux */ }
    }, []);

    // Auto-refresh analytics every 15s if batch is running
    useEffect(() => {
        if (!analytics) return;
        const isScoring = analytics.scored > 0 && analytics.unscored > 0;
        if (!isScoring) return;
        const t = setInterval(() => {
            api.get(`/growth/analytics`).then(({ data }) => setAnalytics(data)).catch(() => {});
        }, 15000);
        return () => clearInterval(t);
    }, [analytics]);

    async function launchBatch() {
        if (!isAdmin) return;
        setBatchRunning(true);
        try {
            const { data } = await api.post(
                `/growth/candidates/score-batch?limit=200&only_unscored=true&concurrency=15`,
                {},
                { timeout: 15 * 1000 },
            );
            if (data?.launched) {
                toast.success(
                    `Scoring lancé sur ${data.total} candidats · les scores s'affichent au fil de l'eau`,
                    { id: "growth-batch-status", duration: 6000 },
                );
            } else {
                toast.info(data?.message || "Aucun candidat à scorer.", { id: "growth-batch-status" });
                setBatchRunning(false);
                return;
            }
            const pollTimer = setInterval(async () => {
                try {
                    const { data: fresh } = await api.get(`/growth/analytics`);
                    setAnalytics(fresh);
                    if ((fresh?.unscored || 0) === 0) {
                        clearInterval(pollTimer);
                        setBatchRunning(false);
                        toast.success(`✅ Scoring terminé — ${fresh.scored} candidats scorés`, {
                            id: "growth-batch-status",
                            duration: 8000,
                        });
                        // Recalcule automatiquement les vagues + signaux
                        try {
                            await api.post(`/growth/compute-waves-and-signals?dry_run=false`);
                            toast.success(`✅ Vagues & signaux forts recalculés`, {
                                id: "growth-waves-status",
                                duration: 6000,
                            });
                        } catch (_) { /* silencieux */ }
                        await load();
                        setNextToCallReloadKey((k) => k + 1);
                    }
                } catch (_) { /* silencieux */ }
            }, 4000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de lancer le scoring", {
                id: "growth-batch-status",
            });
            setBatchRunning(false);
        }
    }

    // iter106 · Callback quand une action est posée sur le prochain candidat
    const handleNextActionDone = useCallback(() => {
        // Refresh analytics pour compter la file mise à jour
        api.get(`/growth/analytics`).then(({ data }) => setAnalytics(data)).catch(() => {});
    }, []);

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="growth-candidates-page">
            {/* HEADER */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 text-purple-700 border border-purple-200 text-[10px] font-black uppercase tracking-widest mb-2">
                        <Sparkles className="h-3 w-3" /> Parcours Growth · Prérec 2.0
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-shariti-black tracking-tight">
                        Candidatures Growth
                    </h1>
                    <p className="text-base font-semibold text-slate-500 mt-1">
                        {analytics?.total || 0} candidats · Business Development & Growth Associate (Alternance)
                    </p>
                </div>
                {isAdmin && analytics && analytics.unscored > 0 && (
                    <button
                        onClick={launchBatch}
                        disabled={batchRunning}
                        data-testid="growth-launch-batch-btn"
                        className="btn-shariti disabled:opacity-50 inline-flex items-center gap-2"
                    >
                        {batchRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                        {batchRunning
                            ? `Analyse en cours · ${analytics.scored}/${analytics.total}`
                            : `Analyser les ${analytics.unscored} candidatures restantes`}
                    </button>
                )}
                <Link to="/growth/traites" data-testid="growth-traites-link"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-black border-2 border-slate-200 text-slate-600 hover:border-shariti-orange hover:text-shariti-orange transition">
                    <FileText className="h-3.5 w-3.5" />Candidats traités
                </Link>
            </div>

            {/* iter109 · BADGE RAPPELS — visible dès qu'il y a des rappels dus */}
            {analytics?.recalls && (analytics.recalls.due_today > 0 || analytics.recalls.overdue > 0) && (
                <div className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="growth-recalls-badge">
                    <div className="flex items-center gap-3">
                        <CalendarClock className="h-6 w-6 text-blue-600" />
                        <div>
                            <div className="text-sm font-black text-blue-900">
                                {analytics.recalls.due_today > 0 && (
                                    <span data-testid="growth-recalls-due-today">🔵 {analytics.recalls.due_today} rappel{analytics.recalls.due_today > 1 ? "s" : ""} aujourd'hui</span>
                                )}
                                {analytics.recalls.due_today > 0 && analytics.recalls.overdue > 0 && <span> · </span>}
                                {analytics.recalls.overdue > 0 && (
                                    <span className="text-red-600" data-testid="growth-recalls-overdue">⏰ {analytics.recalls.overdue} en retard</span>
                                )}
                            </div>
                            <p className="text-xs font-semibold text-blue-700 mt-0.5">
                                Les rappels échus passent automatiquement en tête de la file d'appels.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* iter107 · BANDEAU STATS COMPACT (4 catégories doctrinales) */}
            {analytics && (
                <div className="rounded-2xl bg-white border-2 border-slate-100 p-4" data-testid="growth-stats-bandeau">
                    {/* Ligne 1 · totaux + à préqualifier */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="rounded-xl p-3 border bg-slate-50 text-slate-700 border-slate-200">
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Total</div>
                            <div className="text-3xl font-black mt-1" data-testid="growth-stat-total">{analytics.total}</div>
                        </div>
                        <div className="rounded-xl p-3 border bg-blue-50 text-blue-700 border-blue-200">
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">Analysés</div>
                            <div className="text-3xl font-black mt-1" data-testid="growth-stat-scored">{analytics.scored}</div>
                            {batchRunning && analytics.total > 0 && (
                                <div className="text-[10px] font-semibold text-blue-600 mt-0.5">
                                    {analytics.scored} / {analytics.total} · {Math.round(analytics.scored / analytics.total * 100)}%
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl p-3 border bg-slate-100 text-slate-500 border-slate-200">
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">À analyser</div>
                            <div className="text-3xl font-black mt-1" data-testid="growth-stat-unscored">{analytics.unscored}</div>
                        </div>
                        <div className="rounded-xl p-3 border-2 bg-gradient-to-br from-shariti-orange/10 to-rose-50 text-shariti-orange border-shariti-orange/40">
                            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">À appeler</div>
                            <div className="text-3xl font-black mt-1" data-testid="growth-stat-callable">
                                {analytics.total_callable}
                            </div>
                            <div className="text-[10px] font-semibold text-shariti-orange/80 mt-0.5">
                                TOP + Très prometteur + Potentiel · {analytics.prequal_progress?.done || 0} appelés
                            </div>
                        </div>
                    </div>

                    {/* Ligne 2 · 4 catégories doctrinales (iter107) */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                        {CATEGORY_STATS.map((c) => (
                            <div
                                key={c.key}
                                data-testid={`growth-category-${c.key}`}
                                className={`rounded-xl p-3 border ${c.tone}`}
                            >
                                <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1.5">
                                    <span>{c.emoji}</span>
                                    <span>{c.label}</span>
                                </div>
                                <div className="text-2xl font-black mt-1 leading-none">
                                    {analytics.by_category?.[c.key] || 0}
                                </div>
                                <div className="text-[10px] font-semibold opacity-60 mt-0.5">
                                    Score {c.range}
                                    {analytics.scored > 0 && (
                                        <> · {Math.round((analytics.by_category?.[c.key] || 0) / analytics.scored * 100)}%</>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* iter106 · PROCHAIN CANDIDAT À APPELER */}
            <NextToCallBlock key={nextToCallReloadKey} analytics={analytics} onActionDone={handleNextActionDone} />

            {/* iter108 · SECTION "TOUS LES CANDIDATS" — bloc unifié titre + filtres + liste */}
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-5" data-testid="growth-all-candidates-section">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                    <h2 className="text-xl font-black text-shariti-black tracking-tight uppercase">
                        Tous les candidats
                        <span className="ml-2 text-shariti-orange">— {analytics?.total || 0}</span>
                    </h2>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                        Vivier complet · analysés & à analyser
                    </span>
                </div>

                {/* FILTERS — rattachés visuellement à la liste ci-dessous */}
                <div className="flex flex-wrap gap-2 items-center pb-3 border-b border-slate-100 mb-4">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-1">
                        Filtrer :
                    </span>
                    {CATEGORY_FILTERS.map((f) => {
                        const isActive = filter === f.key;
                        return (
                            <button
                                key={f.key || "all"}
                                onClick={() => setFilter(f.key)}
                                data-testid={`growth-filter-${f.key || "all"}`}
                                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold transition border ${
                                    isActive
                                        ? "bg-shariti-orange text-white border-shariti-orange"
                                        : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                }`}
                            >
                                {f.emoji && <span>{f.emoji}</span>}
                                {f.label}
                            </button>
                        );
                    })}
                </div>

                {/* LIST */}
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-slate-400">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : candidates.length === 0 ? (
                    <div className="text-center py-16 rounded-2xl bg-slate-50 border border-slate-100">
                        <Sparkles className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="font-bold text-slate-500">
                            {filter ? "Aucun candidat dans cette catégorie" : "Aucun candidat Growth pour l'instant"}
                        </p>
                        {analytics?.unscored > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                                {analytics.unscored} candidat(s) à scorer — clique sur "Scorer les non scorés"
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {candidates.map((c, i) => (
                            <GrowthCandidateCard key={c.id} candidate={c} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
